// Live voice session for a deity: fetch an ephemeral Realtime secret, connect a
// transport, relay `ask_deity` tool calls to /api/ai/deity, keep a transcript.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { apiFetch } from '../lib/api';
import { askAgent } from './askAgent';
import { openCouncil, openScreen, requestHandOff, resolveSeat } from './appActions';
import { AvatarBridge, AVATAR_BASE } from './AvatarBridge';
import { createVoiceTransport } from './index';
import { describeScreen, subscribeScreen } from './screenContext';
import type { LiveConnState, LiveEvents, VoiceTransport } from './VoiceTransport';

export interface LiveLine {
  role: 'user' | 'deity';
  text: string;
}

/** 'puppet' = zero-server viseme puppet (web); anything else = renderer bridge. */
export const AVATAR_MODE: 'puppet' | 'bridge' = process.env.EXPO_PUBLIC_AVATAR_MODE === 'puppet' ? 'puppet' : 'bridge';

export type AvatarState = 'off' | 'connecting' | 'on' | 'failed';

/** Pause after an app-action tool result so the deity's spoken line ("Connecting you to ATLAS.") finishes first. */
const ACTION_GRACE_MS = 1600;

export function useLiveVoice({
  toolId,
  onToolCall,
  intro,
}: {
  toolId: string;
  onToolCall?: (name: string, args: Record<string, unknown>) => void;
  /** Set when this call was handed over from another speaker: the deity greets first with this context. */
  intro?: string;
}) {
  const [state, setState] = useState<LiveConnState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [deitySpeaking, setDeitySpeaking] = useState(false);
  const [userSpeaking, setUserSpeaking] = useState(false);
  const [muted, setMuted] = useState(false);
  const [transcript, setTranscript] = useState<LiveLine[]>([]);

  const [avatarStream, setAvatarStream] = useState<MediaStream | null>(null);
  const [avatarState, setAvatarState] = useState<AvatarState>('off');
  const [puppetTrack, setPuppetTrack] = useState<MediaStreamTrack | null>(null);

  const bridge = useRef<AvatarBridge | null>(null);
  const transport = useRef<VoiceTransport | null>(null);
  const conversationId = useRef<string | undefined>(undefined);
  const mutedRef = useRef(false);
  const session = useRef(0);
  const deityOpen = useRef(false); // last transcript entry is an in-progress deity line
  const onToolCallRef = useRef(onToolCall);
  onToolCallRef.current = onToolCall;
  const introRef = useRef(intro);
  // KYRS Audit runs on the device, not through the voice model's judgement: once the user says "audit", every
  // final answer goes straight to KYRS's agent (which runs the protocol and writes the tasks) and the voice
  // only reads back what KYRS says.
  const auditing = useRef(false);

  const dropAvatar = useCallback((t: VoiceTransport | null, next: AvatarState) => {
    const b = bridge.current;
    bridge.current = null;
    b?.disconnect();
    t?.setDirectPlayback(false); // avatar gone: voice plays directly again
    setAvatarStream(null);
    setPuppetTrack(null);
    setAvatarState(next);
  }, []);

  const end = useCallback(() => {
    session.current++;
    dropAvatar(transport.current, 'off');
    const t = transport.current;
    transport.current = null;
    t?.disconnect();
    deityOpen.current = false;
    auditing.current = false;
    setDeitySpeaking(false);
    setUserSpeaking(false);
    setState((s) => (s === 'idle' ? s : 'ended'));
  }, []);

  const start = useCallback(async () => {
    const id = ++session.current;
    transport.current?.disconnect();
    transport.current = null;
    const live = () => id === session.current;
    setError(null);
    setState('connecting');
    setDeitySpeaking(false);
    setUserSpeaking(false);
    setMuted(false);
    mutedRef.current = false;
    deityOpen.current = false;
    auditing.current = false;
    dropAvatar(null, 'off');
    const t = createVoiceTransport();
    transport.current = t;

    const relayAudit = async (said: string) => {
      t.interrupt(); // drop whatever the voice model started saying on its own
      let reply: string;
      try {
        const r = await askAgent(toolId, said, conversationId.current);
        if (r.conversationId) conversationId.current = r.conversationId;
        reply = r.text || 'Saved.';
      } catch (e) {
        reply = `That answer failed to save (${e instanceof Error ? e.message : 'error'}). Say it again?`;
      }
      if (!live()) return;
      t.sendContext(
        `AUDIT IN PROGRESS. The app relays every user answer to KYRS itself: do NOT call ask_deity and do not add anything. ` +
          `Say exactly this to the user, word for word, then stop and listen: "${reply.replace(/\*\*|__/g, '')}"`,
        true,
      );
    };

    const ev: LiveEvents = {
      onState: (s, err) => {
        if (!live()) return;
        setState(s);
        if (s === 'connected') {
          t.sendContext(`What the user is looking at in the app right now:\n${describeScreen()}`);
          if (introRef.current) {
            t.sendContext(introRef.current, true);
            introRef.current = undefined;
          }
        }
        if (s === 'error') {
          setError(err ?? 'Live voice failed');
          setDeitySpeaking(false);
        }
      },
      onSpeaking: (v) => live() && setDeitySpeaking(v),
      onUserSpeaking: (v) => live() && setUserSpeaking(v),
      onBargeIn: () => {
        if (live()) bridge.current?.flush();
      },
      onRemoteAudioTrack: (track) => {
        if (!live() || Platform.OS !== 'web') return;
        if (AVATAR_MODE === 'puppet') {
          // Voice stays on direct playback (instant); the puppet only taps the same track.
          setPuppetTrack(track);
          setAvatarState('on');
          return;
        }
        if (bridge.current) return;
        void (async () => {
          if (!(await AvatarBridge.probe(AVATAR_BASE))) return; // renderer not running: stay in glow mode
          if (!live() || bridge.current) return;
          const b = new AvatarBridge();
          bridge.current = b;
          setAvatarState('connecting');
          b.onClosed = () => {
            if (bridge.current === b && live()) dropAvatar(t, 'failed');
          };
          try {
            const { videoStream } = await b.connect(track, AVATAR_BASE);
            if (!live() || bridge.current !== b) return;
            t.setDirectPlayback(true); // renderer's audio plays instead, keeping lips in sync
            setAvatarStream(videoStream);
            setAvatarState('on');
          } catch {
            if (bridge.current === b) dropAvatar(t, 'failed');
          }
        })();
      },
      onTranscript: (role, text, final) => {
        if (!live()) return;
        if (role === 'user') {
          if (final && text) {
            setTranscript((p) => [...p, { role, text }]);
            if (toolId === 'kyrs' && (auditing.current || /\baudit/i.test(text))) {
              const first = !auditing.current;
              auditing.current = true;
              void relayAudit(first ? 'Run an audit.' : text);
            }
          }
          return;
        }
        setTranscript((p) => {
          const next = p.slice();
          const last = next[next.length - 1];
          if (deityOpen.current && last?.role === 'deity') {
            next[next.length - 1] = { role, text: final ? text || last.text : last.text + text };
          } else if (final) {
            if (text) next.push({ role, text });
            deityOpen.current = false;
            return next;
          } else {
            next.push({ role, text });
            deityOpen.current = true;
            return next;
          }
          if (final) deityOpen.current = false;
          return next;
        });
      },
      onToolCall: (callId, name, args) => {
        if (!live()) return;
        onToolCallRef.current?.(name, args);
        const isSyqi = toolId === 'syqi';
        const me = isSyqi ? 'SyQi' : resolveSeat(toolId)?.name ?? toolId.toUpperCase();
        // ---- app actions (handled here, on the device) ----
        if (name === 'open_screen') {
          const opened = openScreen(args.target);
          t.sendToolResult(
            callId,
            opened
              ? `Opened ${opened} on the user's screen. The call is still connected; carry on.`
              : `"${String(args.target)}" is not a screen. Valid: atlas, kyrs, krnos, metis, clio, tkne, numa, poros, moyri, hrmez, themis, janus, argos, nemos, astra, epic, sofia, settings, pantheon, home, council.`,
          );
          return;
        }
        if (name === 'hand_off') {
          const to = resolveSeat(args.deity);
          if (!to) {
            t.sendToolResult(callId, `"${String(args.deity)}" is not a deity. Ask the user who they want.`);
            return;
          }
          if (to.id === (isSyqi ? 'syqi' : resolveSeat(toolId)?.id)) {
            t.sendToolResult(callId, `You are already ${to.name}; just keep talking.`);
            return;
          }
          t.sendToolResult(callId, `Connecting the user to ${to.name} now. Do not say anything else.`);
          setTimeout(() => {
            if (live()) requestHandOff({ id: to.id, name: to.name, from: me });
          }, ACTION_GRACE_MS);
          return;
        }
        if (name === 'start_council') {
          const members = Array.isArray(args.deities)
            ? [...new Set((args.deities as unknown[]).map((d) => resolveSeat(d)?.id).filter((d): d is string => !!d))]
            : [];
          const topic = typeof args.topic === 'string' ? args.topic : undefined;
          t.sendToolResult(callId, 'The Council room is opening and this call ends now. Do not say anything else.');
          setTimeout(() => {
            if (!live()) return;
            openCouncil({ members: members.length ? (members.includes('syqi') ? members : ['syqi', ...members]) : undefined, topic });
          }, ACTION_GRACE_MS);
          return;
        }
        if (name !== (isSyqi ? 'ask_syqi' : 'ask_deity')) {
          t.sendToolResult(callId, 'Unknown tool.');
          return;
        }
        if (auditing.current) {
          t.sendToolResult(callId, 'The app is relaying the audit to KYRS itself. Say nothing.');
          return;
        }
        void (async () => {
          // A failed request must be voiced as a failure, never as "no access": the old
          // "records could not be reached" text made the voice model tell the user she
          // couldn't access KYRS when the server had simply errored.
          let out: string;
          try {
            const r = await askAgent(toolId, String(args.question ?? ''), conversationId.current);
            if (r.conversationId) conversationId.current = r.conversationId;
            out = r.text || 'Done.';
            if (r.applied.length) out += `\n(Done on screen: ${r.applied.join('; ')})`;
          } catch (e) {
            const why = e instanceof Error ? e.message : 'unknown error';
            out =
              `REQUEST FAILED (${why}). You DO have access; this was a temporary error and nothing was confirmed as changed. ` +
              `Tell the user plainly that the request just failed with an error (not that you lack access), and offer to try again.`;
          }
          if (live()) t.sendToolResult(callId, out);
        })();
      },
    };

    try {
      const creds = await apiFetch<{ clientSecret: string; expiresAt?: string; model: string }>('/api/live/session', {
        method: 'POST',
        body: JSON.stringify({ toolId }),
      });
      if (!live()) return;
      await t.connect({ clientSecret: creds.clientSecret, model: creds.model }, ev);
    } catch (e) {
      if (!live()) return;
      transport.current = null;
      t.disconnect();
      setError(e instanceof Error ? e.message : 'Could not start live voice');
      setState('error');
    }
  }, [toolId]);

  // Keep the voice model current as the user moves between screens mid-call.
  useEffect(
    () =>
      subscribeScreen(() => {
        if (state === 'connected') transport.current?.sendContext(`The user moved to another screen. Now looking at:\n${describeScreen()}`);
      }),
    [state],
  );

  const toggleMute = useCallback(() => {
    mutedRef.current = !mutedRef.current;
    setMuted(mutedRef.current);
    transport.current?.setMuted(mutedRef.current);
  }, []);

  const interrupt = useCallback(() => {
    bridge.current?.flush();
    transport.current?.interrupt();
  }, []);

  useEffect(() => () => {
    session.current++;
    bridge.current?.disconnect();
    bridge.current = null;
    transport.current?.disconnect();
    transport.current = null;
  }, []);

  return { state, error, deitySpeaking, userSpeaking, muted, transcript, avatarStream, avatarState, puppetTrack, start, end, toggleMute, interrupt };
}
